<template>
    <div class="pipeline-stage-move-goal-modal">
        <GoalModal
            :is-open="isOpen"
            :title="$t('funnel.edit.sideBar.stage.move.title')"
            :summary-text="$t('funnel.edit.sideBar.stage.move.summary')"
            :goal-tip-text="$t('funnel.edit.sideBar.stage.move.tip.overview')"
            :goal-tip-link="$t('funnel.edit.sideBar.stage.move.tip.url')"
            :cell="cell"
            :cell-type="cellType"
            @update-config="updateConfig"
            @close-modal="closeModal"
        >
            <template #goalParameters>
                <div class="vertical-container">
                    <DsMultiselect
                        v-model="movingSelected"
                        required
                        bind-value-only
                        :options="movingOptions"
                        :placeholder="$t('funnel.edit.sideBar.stage.move.when.moving.placeholder')"
                        class="moving-direction-selected-class"
                        :force-invalid="emptyMoving"
                    >
                        <template #help>
                            <span v-if="emptyMoving" data-qa="invalid-moving-type">
                                {{ $t('global.field.required') }}
                            </span>
                        </template>
                    </DsMultiselect>

                    <DsMultiselect
                        v-model="pipelineSelected"
                        required
                        bind-value-only
                        :options="pipelineOptions"
                        :placeholder="$t('funnel.edit.sideBar.stage.move.pipeline.placeholder')"
                        class="pipeline-selected-class"
                        :force-invalid="emptyPipeline"
                        @input="loadStageOptions"
                    >
                        <template #help>
                            <span v-if="emptyPipeline" data-qa="invalid-pipeline-type">
                                {{ $t('global.field.required') }}
                            </span>
                        </template>
                    </DsMultiselect>

                    <DsMultiselect
                        v-model="stageSelected"
                        required
                        bind-value-only
                        :options="stageOptions"
                        :force-invalid="emptyStage"
                        :placeholder="$t('funnel.edit.sideBar.stage.move.stage.placeholder')"
                        class="stage-selected-class"
                    >
                        <template #help>
                            <span v-if="emptyStage" data-qa="invalid-stage-type">
                                {{ $t('global.field.required') }}
                            </span>
                        </template>
                    </DsMultiselect>
                </div>
            </template>
        </GoalModal>
    </div>
</template>

<script>
export const ID_STR_PREFIX = 'id-';

import { getPipelineConfiguration } from './pipelineStageMove.api';
import { GOAL_PIPELINE_STAGE_MOVED } from '../../../utils/editor.constants';
import GoalModal from '../goalModal/GoalModal.vue';

export default {
    ID_STR_PREFIX,
    name: 'PipelineStageMove',
    components: {
        GoalModal,
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        cell: {
            type: Object,
            default: () => ({
                value: {
                    stageMoveDirection: null,
                    stageId: null,
                },
            }),
        },
    },
    emits: ['close-modal', 'update-cell-value'],
    data() {
        return {
            cellType: GOAL_PIPELINE_STAGE_MOVED,
            movingSelected: null,
            pipelineSelected: null,
            stageSelected: null,
            movingOptions: [
                { value: 'IN', label: this.$t('funnel.edit.sideBar.stage.move.when.moving.option.into') },
                { value: 'OUT', label: this.$t('funnel.edit.sideBar.stage.move.when.moving.option.outof') },
            ],
            pipelineLists: [],
            pipelineOptions: [],
            stageOptions: [],
            emptyMoving: false,
            emptyPipeline: false,
            emptyStage: false,
        };
    },
    mounted() {
        this.loadConfiguration();
    },

    methods: {
        updateConfig() {
            if (this.isValid()) {
                this.saveCellValue();
            }
        },

        isEmptyMoving() {
            this.emptyMoving = !this.movingSelected;
        },

        isEmptyPipeline() {
            this.emptyPipeline = !this.pipelineSelected;
        },

        isEmptyStage() {
            this.emptyStage = !this.stageSelected;
        },

        isValid() {
            this.isEmptyMoving();
            this.isEmptyPipeline();
            this.isEmptyStage();

            return !this.emptyMoving && !this.emptyPipeline && !this.emptyStage;
        },

        saveCellValue() {
            try {
                const cell = { ...this.cell };

                cell.value = cell.value || {};

                cell.value.stageMoveDirection = this.movingSelected;
                cell.value.stageId = `${this.$options.ID_STR_PREFIX}${this.stageSelected}`;

                this.$emit('update-cell-value', { cell });
            } catch (error) {
                const errorMessage = this.$t('funnel.edit.sideBar.goal.apply.note.create.error');

                this.$error({
                    message: errorMessage,
                });
            }
        },
        closeModal() {
            this.$emit('close-modal', GOAL_PIPELINE_STAGE_MOVED);
        },
        async loadConfiguration() {
            const tempStageMoveDirection = this.cell?.value?.stageMoveDirection;
            const tempStageId = this.cell?.value?.stageId;

            if (tempStageMoveDirection) {
                this.movingSelected = tempStageMoveDirection;
            }

            if (tempStageId) {
                this.stageSelected = tempStageId;
            }

            const { pipelines } = await getPipelineConfiguration();

            this.pipelineLists = [...pipelines];

            this.pipelineOptions = pipelines?.map((p) => ({
                value: p.id,
                label: p.name,
                icon: 'pipeline',
            }));

            if (tempStageId && typeof tempStageId === 'string' && tempStageId.startsWith(this.$options.ID_STR_PREFIX)) {
                this.stageSelected = tempStageId.substring(this.$options.ID_STR_PREFIX.length);

                this.pipelineSelected = this.findParentId(this.stageSelected);

                if (this.pipelineSelected) {
                    this.stageOptions = pipelines.find((p) => p.id === this.pipelineSelected)?.stages.map((st) => ({
                        value: st.id,
                        label: st.name,
                    }));
                }
            }
        },
        loadStageOptions() {
            if (this.pipelineSelected) {
                const pipeline = this.pipelineLists.find((p) => p.id === this.pipelineSelected);

                this.stageOptions = pipeline?.stages?.map((st) => ({
                    value: st.id,
                    label: st.name,
                }));
            }
        },
        findParentId(stageId) {
            const pipelineWithStage = this.pipelineLists.find((p) => p.stages.some((s) => {
                return s.id === stageId;
            }));

            return pipelineWithStage?.id;
        },
    },
};
</script>

<style lang="scss" scoped>
.vertical-container {
    display: flex;
    flex-direction: column;
}


</style>
